import { useRef, type PointerEvent } from 'react';

/** Longest gap between two taps or clicks that still counts as a double. */
export const DOUBLE_TAP_MS = 500;

/** Furthest, in px, a tap can travel, or two taps can land apart, and still count. */
export const DOUBLE_TAP_SLOP_PX = 16;

/** The parts of a pointer event a double tap is judged on. */
export type TapEvent = Pick<
  PointerEvent<Element>,
  'timeStamp' | 'clientX' | 'clientY' | 'button' | 'isPrimary'
>;

/** Pointer-down and pointer-up handlers for {@link useDoubleTap}. */
export interface DoubleTapHandlers {
  down: (event: TapEvent) => void;
  up: (event: TapEvent) => void;
}

interface Point {
  at: number;
  x: number;
  y: number;
}

const pointOf = (event: TapEvent): Point => ({
  at: event.timeStamp,
  x: event.clientX,
  y: event.clientY,
});

const near = (a: Point, b: Point): boolean =>
  Math.hypot(a.x - b.x, a.y - b.y) <= DOUBLE_TAP_SLOP_PX;

/**
 * Calls `onDouble` on a second primary tap or click close in time and place to the first, the same for mouse, pen
 * and touch (mobile browsers don't reliably fire `dblclick` for a double-tap). A press that moved, such as a drag,
 * isn't a tap.
 */
export const useDoubleTap = (onDouble: () => void): DoubleTapHandlers => {
  const pressed = useRef<Point | null>(null);
  const lastTap = useRef<Point | null>(null);
  return {
    down: (event) => {
      pressed.current =
        event.isPrimary && event.button === 0 ? pointOf(event) : null;
    },
    up: (event) => {
      const start = pressed.current;
      pressed.current = null;
      const tap = pointOf(event);
      if (!start || !near(start, tap)) {
        lastTap.current = null;
        return;
      }
      const prev = lastTap.current;
      const isDouble =
        prev !== null && tap.at - prev.at <= DOUBLE_TAP_MS && near(prev, tap);
      lastTap.current = isDouble ? null : tap;
      if (isDouble) onDouble();
    },
  };
};
